import { RyeMainContentFooter } from "../rye/RyeMainContentFooter";
import { RyeBadge } from "../rye/RyeBadge";
import { DollarAmount } from "../common/DollarAmount";
import { BreadTransaction } from "breadcommon";
import { useExchangeRates } from "../firebaseio/ExchangeRatesContext";
import { buildClasses } from "../utils/buildClasses";

export function TransactionsFooter({
  sortedFilteredTransactions,
  selectedTransactionCount = 0,
  selectedTransactionIds,
}: {
  sortedFilteredTransactions: BreadTransaction[];
  selectedTransactionCount?: number;
  selectedTransactionIds?: Set<string>;
}): JSX.Element | null {
  const { convertTransactionsToDefaultCurrency } = useExchangeRates();
  const convertedTransactions = convertTransactionsToDefaultCurrency(
    sortedFilteredTransactions,
  );

  const badge = ({
    label,
    text,
    dollarAmount,
    vibe = "ordinary",
  }: {
    label?: string;
    text?: string;
    dollarAmount?: number;
    vibe?: "ordinary" | "primary";
  }) => (
    <RyeBadge
      label={label}
      text={text}
      size="sm"
      variant="soft"
      vibe={vibe}
      shape="fully-round"
    >
      {dollarAmount !== undefined && (
        <DollarAmount
          n={dollarAmount}
          size={"sm"}
          align="span"
          showDollarSign={true}
          includeDecimals={true}
          showMinus={true}
          monospaceFont={false}
        />
      )}
    </RyeBadge>
  );

  let selectionSum: number | undefined;
  if (selectedTransactionCount > 1 && selectedTransactionIds) {
    selectionSum = 0;
    for (const t of convertedTransactions) {
      if (selectedTransactionIds.has(t.id)) {
        selectionSum += t.amount;
      }
    }
  }

  const selectionBadges =
    selectedTransactionCount > 0 ? (
      <>
        {badge({
          text: `${selectedTransactionCount} selected`,
          vibe: "primary",
        })}
        {selectionSum !== undefined &&
          badge({ label: "Sum", dollarAmount: selectionSum, vibe: "primary" })}
      </>
    ) : null;

  const count = convertedTransactions.length;

  let statsBadges;
  if (count === 0) {
    statsBadges = <>{badge({ label: "Count", text: `${count}` })}</>;
  } else {
    let sum = 0;
    let smallest = convertedTransactions[0].amount;
    let largest = convertedTransactions[0].amount;
    let unreviewedCount = 0;
    for (const t of convertedTransactions) {
      sum += t.amount;
      smallest = Math.abs(t.amount) < Math.abs(smallest) ? t.amount : smallest;
      largest = Math.abs(t.amount) > Math.abs(largest) ? t.amount : largest;
      if (!t.reviewed) unreviewedCount++;
    }
    const avg = sum / count;
    statsBadges = (
      <>
        {badge({ label: "Count", text: `${count}` })}
        {unreviewedCount > 0 &&
          badge({ label: "Unreviewed", text: `${unreviewedCount}` })}
        {badge({ label: "Sum", dollarAmount: sum })}
        {badge({ label: "Smallest", dollarAmount: smallest })}
        {badge({ label: "Largest", dollarAmount: largest })}
        {badge({ label: "Average", dollarAmount: avg })}
      </>
    );
  }

  return (
    <RyeMainContentFooter>
      <div
        className={buildClasses(
          "w-full",
          "flex",
          "justify-between",
          "items-center",
        )}
      >
        <div className={buildClasses("flex", "items-center", "gap-1.5")}>
          {selectionBadges}
        </div>
        <div className={buildClasses("flex", "items-center", "gap-1.5")}>
          {statsBadges}
        </div>
      </div>
    </RyeMainContentFooter>
  );
}
